import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type {
  Vehicle,
  VehicleVerificationCheck,
  VehicleVerificationSummary,
} from '@breaker/shared-types';
import type { CreateVehicleBody, UpdateVehicleBody } from '@breaker/shared-schemas';

/** List rows arrive enriched with the government-check badge summary. */
export type VehicleWithVerification = Vehicle & {
  verification?: VehicleVerificationSummary;
};

export interface VehicleVerificationDetail {
  verification: VehicleVerificationSummary;
  checks: VehicleVerificationCheck[];
}

export function useVehicles(fleetId: string | null) {
  return useQuery({
    queryKey: ['vehicles', fleetId ?? ''] as const,
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ vehicles: VehicleWithVerification[] }>(`/v1/fleets/${fleetId}/vehicles`),
    select: (data) =>
      [...data.vehicles].sort((a, b) => a.registration.localeCompare(b.registration)),
  });
}

export function useVehicleVerification(fleetId: string | null, vehicleId: string | null) {
  return useQuery({
    queryKey: ['vehicles', fleetId ?? '', vehicleId ?? '', 'verification'] as const,
    enabled: Boolean(fleetId && vehicleId),
    queryFn: () =>
      api<VehicleVerificationDetail>(
        `/v1/fleets/${fleetId}/vehicles/${vehicleId}/verification`,
      ),
    // The nightly sweep refreshes rows once a day; polling would be noise.
    staleTime: 30_000,
  });
}

export function useCreateVehicle(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVehicleBody) =>
      api<{ vehicle: Vehicle }>(`/v1/fleets/${fleetId}/vehicles`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles', fleetId ?? ''] }),
  });
}

export function useUpdateVehicle(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, body }: { vehicleId: string; body: UpdateVehicleBody }) =>
      api<{ vehicle: Vehicle }>(`/v1/fleets/${fleetId}/vehicles/${vehicleId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles', fleetId ?? ''] });
      // Ownership changes affect driver default-vehicle eligibility.
      qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] });
    },
  });
}

export function useRequestRecheck(fleetId: string | null, vehicleId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checkType?: 'dvla_ves' | 'dvsa_mot_history') =>
      api<{ enqueued: true }>(
        `/v1/fleets/${fleetId}/vehicles/${vehicleId}/verification/recheck${
          checkType ? `?checkType=${checkType}` : ''
        }`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      // Worker runs async — refetch after a beat so fresh rows land.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['vehicles', fleetId ?? ''] });
      }, 2_500);
    },
  });
}
